import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { CampaignLinkInput } from '@/types';
import { accessTokenExpiringSoon, expireSession, refreshSession } from './session';

const API_URL = import.meta.env.VITE_API_URL;

/** Thrown in place of a 401 once the session is over; pages show it as an error. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired, sign in again.');
    this.name = 'SessionExpiredError';
  }
}

/** Sign-in and refresh calls handle their own 401s. */
function isAuthCall(url?: string): boolean {
  return /\/auth\/(employee\/signin|signin|refresh)\b/.test(url || '');
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests, renewing it first if it's about to expire.
    this.client.interceptors.request.use(async (config) => {
      if (!isAuthCall(config.url) && accessTokenExpiringSoon()) {
        await refreshSession();
      }
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // A 401 means the token was rejected: renew once and retry. If renewing
    // fails, the session is over, so send the user to sign in again instead
    // of letting pages render an empty state.
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
        if (error.response?.status !== 401 || !original || isAuthCall(original.url)) {
          throw error;
        }
        const token = original._retried ? null : await refreshSession();
        if (!token) {
          expireSession();
          throw new SessionExpiredError();
        }
        original._retried = true;
        original.headers.Authorization = `Bearer ${token}`;
        return this.client(original);
      }
    );
  }

  // Generic request methods for auth service
  async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.client.post(url, data);
  }

  async get<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.get(url, config);
  }

  // Protocol Management
  async getPendingProtocols() {
    const response = await this.client.get('/api/v1/admin/protocols/pending');
    return response.data;
  }

  async getAllProtocols(status?: string) {
    const params = status ? { status } : {};
    const response = await this.client.get('/api/v1/admin/protocols', { params });
    return response.data;
  }

  async getProtocolDetail(id: string) {
    const response = await this.client.get(`/api/v1/admin/protocols/${id}`);
    return response.data;
  }

  async approveProtocol(id: string, notes?: string) {
    const response = await this.client.post(`/api/v1/admin/protocols/${id}/approve`, { notes });
    return response.data;
  }

  async rejectProtocol(id: string, reason: string) {
    const response = await this.client.post(`/api/v1/admin/protocols/${id}/reject`, { reason });
    return response.data;
  }

  async reextractProtocol(id: string) {
    const response = await this.client.post(`/api/v1/admin/protocols/${id}/reextract`);
    return response.data;
  }

  async getManifest() {
    const response = await this.client.get('/api/v1/admin/protocols/manifest');
    return response.data;
  }

  async regenerateManifest() {
    const response = await this.client.post('/api/v1/admin/protocols/manifest');
    return response.data;
  }

  // User Management
  async getUsers(params?: { page?: number; pageSize?: number; userType?: string; search?: string }) {
    const response = await this.client.get('/api/v1/admin/users', { params });
    return response.data;
  }

  // Journey drop-off funnel analytics
  async getFunnelAnalytics(params?: { days?: number | 'all' }) {
    const response = await this.client.get('/api/v1/admin/funnel-analytics', { params });
    return response.data;
  }

  // First-party web analytics (page views, sources, journey events)
  async getWebAnalytics(params?: {
    days?: number | 'all';
    internal?: 1;
    source?: string;
    medium?: string;
    campaign?: string;
    touch?: 'first' | 'any';
  }) {
    const response = await this.client.get('/api/v1/admin/web-analytics', { params });
    return response.data;
  }

  // QR short links (voyaxhealth.com/go/<slug>)
  async getCampaignLinks(params?: { days?: number | 'all' }) {
    const response = await this.client.get('/api/v1/admin/campaign-links', { params });
    return response.data;
  }

  async createCampaignLink(input: CampaignLinkInput) {
    const response = await this.client.post('/api/v1/admin/campaign-links', input);
    return response.data;
  }

  async updateCampaignLink(id: string, input: Partial<CampaignLinkInput>) {
    const response = await this.client.put(`/api/v1/admin/campaign-links/${encodeURIComponent(id)}`, input);
    return response.data;
  }

  // Recent visitors (who to investigate) and one visitor's full timeline
  async getRecentVisitors(params?: { days?: number | 'all'; errors?: 1; internal?: 1; limit?: number }) {
    const response = await this.client.get('/api/v1/admin/web-analytics', { params: { view: 'visitors', ...params } });
    return response.data;
  }

  async getSessionTimeline(lookup: { visitor_id?: string; session_id?: string; consultation_id?: string }) {
    const response = await this.client.get('/api/v1/admin/web-analytics', { params: { view: 'timeline', ...lookup } });
    return response.data;
  }

  // CDC Extraction Management
  async getCdcExtractions(params?: { page?: number; limit?: number; status?: string; country_iso2?: string; search?: string }) {
    const response = await this.client.get('/api/v1/admin/cdc-extractions', { params });
    return response.data;
  }

  async getCdcExtractionDetail(id: string) {
    const response = await this.client.get(`/api/v1/admin/cdc-extractions/${id}`);
    return response.data;
  }

  async getCdcExtractionByCountry(iso2: string) {
    const response = await this.client.get(`/api/v1/admin/cdc-extractions/country/${iso2}`);
    return response.data;
  }

  async triggerCdcExtraction(countryIso2: string) {
    const response = await this.client.post('/api/v1/admin/cdc-extractions/trigger', {
      country_iso2: countryIso2,
    });
    return response.data;
  }

  async triggerCdcBatchExtraction() {
    const response = await this.client.post('/api/v1/admin/cdc-extractions/trigger-batch');
    return response.data;
  }

  async getCdcBatchRuns() {
    const response = await this.client.get('/api/v1/admin/cdc-extractions/batches');
    return response.data;
  }

  async uploadCdcCsv(dataType: string, csvContent: string) {
    const response = await this.client.post('/api/v1/admin/cdc-extractions/upload-csv', {
      data_type: dataType,
      csv_content: csvContent,
    });
    return response.data;
  }

  async getCdcSupplementalData(dataType?: string) {
    const params = dataType ? { data_type: dataType } : {};
    const response = await this.client.get('/api/v1/admin/cdc-extractions/supplemental', { params });
    return response.data;
  }

  async getCdcMissingCountries() {
    const response = await this.client.get('/api/v1/admin/cdc-extractions/missing');
    return response.data;
  }

  async retryCdcMissingCountries() {
    const response = await this.client.post('/api/v1/admin/cdc-extractions/retry-missing');
    return response.data;
  }

  // Blog Post Management
  async getBlogPosts(status?: string) {
    const params = status ? { status } : {};
    const response = await this.client.get('/api/v1/admin/blog-posts', { params });
    return response.data;
  }

  async getBlogPost(id: string) {
    const response = await this.client.get(`/api/v1/admin/blog-posts/${id}`);
    return response.data;
  }

  async createBlogPost(data: {
    title?: string;
    slug?: string;
    excerpt?: string;
    body?: string;
    category?: string;
    status?: string;
  }) {
    const response = await this.client.post('/api/v1/admin/blog-posts', data);
    return response.data;
  }

  async updateBlogPost(
    id: string,
    data: {
      title?: string;
      slug?: string;
      excerpt?: string;
      body?: string;
      category?: string;
      status?: string;
    }
  ) {
    const response = await this.client.put(`/api/v1/admin/blog-posts/${id}`, data);
    return response.data;
  }

  async publishBlogPost(id: string) {
    const response = await this.client.post(`/api/v1/admin/blog-posts/${id}/publish`);
    return response.data;
  }

  async deleteBlogPost(id: string) {
    const response = await this.client.delete(`/api/v1/admin/blog-posts/${id}`);
    return response.data;
  }
}

export const api = new ApiService();
export const protocolService = api;
export default api;
